import { GoogleAuthProvider, EmailAuthProvider, linkWithPopup, linkWithRedirect, getRedirectResult, signInWithCredential, signInWithCustomToken, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, linkWithCredential, type AuthError, type User, type UserCredential } from 'firebase/auth';
import { auth, ensureUser, idToken } from '../firebase';
import { handoffFromUrl, handoffViaFromUrl, withHandoff, withoutHandoff } from './handoff';
import { inAppBrowser } from './in-app-browser';
import { writeUserDoc } from './feed';
import { loadProfile } from './state';
import { track } from '../analytics';

/**
 * The soft save (10 §6, 16 §2): after the first card, the anonymous identity
 * is linked in place to Google or to an email, so nothing moves and the feed
 * becomes the person's. Nothing here is a wall; it is offered, not required.
 *
 * Link, or sign in (16 §5). When the Google account or the email already
 * belongs to a feed, this device signs into that feed instead of failing. The
 * anonymous uid on this device is left behind; the adopt step (16 §5) is what
 * will carry its work across. `email` reaches the user doc only after the
 * link or sign-in has proved it, which is what lets the reminder cron trust it.
 *
 * Across browsers (the handoff). The email link opens wherever Mail opens
 * links, and for everyone who came from an ad that is not Facebook's browser,
 * where the work is. So the save mints a code for this anonymous uid and the
 * link carries it; the browser that opens it signs in as the same uid before
 * anything else happens (adoptHandoffIfPresent), and the email is linked to it
 * there. Same uid, same work, nothing to merge.
 */
export type SaveOutcome = 'saved' | 'signed_in' | 'cancelled' | 'failed' | 'email_sent';

const EMAIL_KEY = 'kindling.saveEmail';
/** Set when a link attempt found the email already has a feed and a fresh link was sent to open it. */
const SIGNIN_KEY = 'kindling.saveEmail.signin';
/** The handoff code this browser minted, or adopted: opening it here again is a no-op. */
const HANDOFF_KEY = 'kindling.handoff';

const BACKEND = ((import.meta.env.VITE_BACKEND_URL as string | undefined) || '').replace(/\/$/, '');

/**
 * Held by nothing but a browser: anonymous, or carried here by a handoff (a
 * custom-token sign-in, which Firebase does not call anonymous) and not linked
 * to Google or an email since.
 */
const unsaved = (u: User) => u.isAnonymous || u.providerData.length === 0;

export function isSaved(): boolean {
  const u = auth.currentUser;
  return !!u && !unsaved(u);
}

/** The address a saved feed is kept under, for the Friends screen. */
export function savedAs(): string | null {
  const u = auth.currentUser;
  return u && !unsaved(u) ? u.email : null;
}

/** A home-screen web app cannot finish a popup; the redirect flow needs VITE_FIREBASE_AUTH_DOMAIN to be this app's own domain (README). */
function needsRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
}

const code = (err: unknown) => (err as AuthError | undefined)?.code || '';
const hadLocalWork = () => loadProfile().madeDays > 0;

/** After a link or sign-in the token carries the verified email; the rules accept `email` only from such a token. */
async function recordSaved(res: UserCredential): Promise<void> {
  await res.user.getIdToken(true);
  await writeUserDoc({ email: res.user.email || undefined, name: res.user.displayName?.split(' ')[0] || undefined });
}

/** The Google account already has a feed: open it here. */
async function signInInstead(err: unknown, via: 'google'): Promise<SaveOutcome> {
  const cred = GoogleAuthProvider.credentialFromError(err as AuthError);
  if (!cred) return 'failed';
  try {
    const res = await signInWithCredential(auth, cred);
    await recordSaved(res);
    track('signed_in', { via, had_local_work: hadLocalWork() });
    return 'signed_in';
  } catch (err2) {
    console.warn('[kindling] sign-in after link failed', err2);
    return 'failed';
  }
}

function afterGoogle(err: unknown): Promise<SaveOutcome> | SaveOutcome {
  const c = code(err);
  if (c.includes('popup-closed') || c.includes('cancelled') || c === 'auth/popup-blocked') return 'cancelled';
  if (c === 'auth/credential-already-in-use') return signInInstead(err, 'google');
  console.warn('[kindling] google link failed', err);
  return 'failed';
}

/**
 * Load the Google popup's iframe now, before the button is tapped. Auth no
 * longer loads it at startup (firebase.ts), and on a phone or in Safari a popup
 * opened after that load has finished can be past the tap's permission and get
 * blocked. Uses auth's own resolver, which has no public handle to warm it.
 */
export function warmGoogleSave(): void {
  try {
    const resolver = (auth as unknown as { _popupRedirectResolver?: { _initialize?: (a: unknown) => Promise<unknown> } })._popupRedirectResolver;
    resolver?._initialize?.(auth)?.catch(() => { /* the popup retries the load itself */ });
  } catch { /* a slower popup at worst */ }
}

export async function saveWithGoogle(): Promise<SaveOutcome> {
  const user = await ensureUser();
  const provider = new GoogleAuthProvider();
  try {
    if (needsRedirect()) { await linkWithRedirect(user, provider); return 'cancelled'; } // the page is leaving; finishRedirectIfPresent picks it up
    const res = await linkWithPopup(user, provider);
    await recordSaved(res);
    return 'saved';
  } catch (err) {
    return afterGoogle(err);
  }
}

/** Called on load, every time: completes a redirect-flow link if one is in flight. */
export async function finishRedirectIfPresent(): Promise<SaveOutcome | null> {
  try {
    const res = await getRedirectResult(auth);
    if (!res) return null;
    await recordSaved(res);
    track('account_saved', { via: 'google_redirect' });
    return 'saved';
  } catch (err) {
    if (code(err) === 'auth/no-auth-event') return null;
    const out = await afterGoogle(err);
    if (out === 'signed_in') track('account_saved', { via: 'google_redirect' });
    return out;
  }
}

const actionSettings = (handoff?: string | null) => ({ url: withHandoff(`${window.location.origin}/?save=1`, handoff, 'email'), handleCodeInApp: true });

/** A code for this anonymous uid, for a link that may open in another browser. Null when the backend cannot: the link still works, in this browser. */
async function mintHandoff(email?: string): Promise<string | null> {
  try {
    const res = await fetch(`${BACKEND}/api/kindling/handoff`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await idToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(email ? { email } : {}),
    });
    if (!res.ok) throw new Error(`handoff ${res.status}`);
    const { code } = (await res.json()) as { code?: unknown };
    if (typeof code !== 'string') throw new Error('handoff: no code');
    try { localStorage.setItem(HANDOFF_KEY, code); } catch { /* ignore */ }
    mintedHere = code;
    return code;
  } catch (err) {
    console.warn('[kindling] handoff not minted; the link will only finish in this browser', err);
    return null;
  }
}

/** The code the last save link carries, for the "open in Safari" hint. */
export function currentHandoff(): string | null {
  try { return localStorage.getItem(HANDOFF_KEY); } catch { return null; }
}

/** Minted on this page, so it is known to be live: one stored by an earlier visit may have expired. */
let mintedHere: string | null = null;
/** A mint for moving already under way (React's dev double effect asks twice). */
let minting: Promise<string | null> | null = null;

/**
 * A code for carrying this feed to the phone's own browser now, without
 * waiting on the email (components/MoveToBrowser.tsx): the one the save link
 * just carried, or a fresh one. Null for a saved feed (it is reached by signing
 * in) or when the backend cannot mint one.
 */
export async function handoffForMove(): Promise<string | null> {
  if (mintedHere) return mintedHere;
  const u = await ensureUser();
  if (!unsaved(u)) return null;
  return (minting ??= mintHandoff().then((c) => { if (!c) minting = null; return c; }));
}

/** Whose feed a handoff carries, for the events either side of it, which PostHog joins on. */
export function currentUid(): string | null {
  return auth.currentUser?.uid ?? null;
}

/** Sends a sign-in link; finishing it (below) links the anonymous account to that email, or opens the feed the email already has. */
export async function saveWithEmail(email: string): Promise<SaveOutcome> {
  try {
    const handoff = await mintHandoff(email);
    // From an in-app browser the link opens somewhere else, and without a code it
    // would finish there on a new, empty account: better no link than that one.
    const iab = inAppBrowser();
    if (!handoff && iab) return 'failed';
    await sendSignInLinkToEmail(auth, email, actionSettings(handoff));
    // the way out of an in-app browser that needs nothing but Mail; landed is handoff_landed, in the other browser
    if (handoff && iab) track('handoff_offered', { via: 'email', in_app: iab, uid: currentUid() });
    try { localStorage.setItem(EMAIL_KEY, email); localStorage.removeItem(SIGNIN_KEY); } catch { /* ignore */ }
    return 'email_sent';
  } catch (err) {
    console.warn('[kindling] email link failed', err);
    return 'failed';
  }
}

/** Called on load: if the URL is an email sign-in link, link the anonymous account to it, or sign into the feed that email already has. */
export async function finishEmailSaveIfPresent(): Promise<SaveOutcome | null> {
  if (typeof window === 'undefined' || !isSignInWithEmailLink(auth, window.location.href)) return null;
  const href = window.location.href;
  let email = ''; let wantsSignIn = false;
  try { email = localStorage.getItem(EMAIL_KEY) || ''; wantsSignIn = localStorage.getItem(SIGNIN_KEY) === '1'; } catch { /* ignore */ }
  if (!email) email = window.prompt('The email you asked us to save your feed to?') || '';
  if (!email) return 'cancelled';
  const done = (out: SaveOutcome) => { window.history.replaceState(null, '', '/'); try { localStorage.removeItem(SIGNIN_KEY); } catch { /* ignore */ } return out; };
  try {
    const user = await ensureUser();
    if (unsaved(user) && !wantsSignIn) {
      try {
        const res = await linkWithCredential(user, EmailAuthProvider.credentialWithLink(email, href));
        await recordSaved(res);
        track('account_saved', { via: 'email' });
        return done('saved');
      } catch (err) {
        if (code(err) !== 'auth/email-already-in-use') throw err;
        // that email already has a feed: open it on this device instead
        try {
          const res = await signInWithEmailLink(auth, email, href);
          await recordSaved(res);
          track('signed_in', { via: 'email', had_local_work: hadLocalWork() });
          return done('signed_in');
        } catch (err2) {
          if (code(err2) !== 'auth/invalid-action-code') throw err2;
          // the link was spent on the failed link attempt: send a fresh one that signs in
          try { localStorage.setItem(SIGNIN_KEY, '1'); } catch { /* ignore */ }
          await sendSignInLinkToEmail(auth, email, actionSettings());
          window.history.replaceState(null, '', '/');
          return 'email_sent';
        }
      }
    }
    const res = await signInWithEmailLink(auth, email, href);
    await recordSaved(res);
    track('signed_in', { via: 'email', had_local_work: hadLocalWork() });
    return done('signed_in');
  } catch (err) {
    console.warn('[kindling] finishing email save failed', err);
    return done('failed');
  }
}

/**
 * Called on load, first: the url carries a handoff code (an email link opened
 * in Safari, or Facebook's ⋯ → Open in external browser), so become the
 * anonymous uid it was minted for before boot decides this is somebody new and
 * pushes an empty road over theirs. True when this browser is now that uid.
 *
 * Left alone: the browser that minted the code (it is that uid already), and
 * a browser with a saved feed open (the email link, if this is one, decides
 * what happens there, as it always has).
 *
 * 'stuck' is a code the backend could not redeem just now (a 5xx, or no
 * answer), even after retrying. The code stays in the url and this browser is
 * not made somebody new: the work is still in the browser it came from, and
 * dropping the code here used to start them on the intro with nothing
 * (2026-10-31, Orlando: every redeem 500'd for want of an IAM role). App.tsx
 * shows HandoffStuck instead. A code the backend refused (gone, used, kept) is
 * 'none', as before: retrying it cannot help.
 */
export type HandoffOutcome = 'adopted' | 'none' | 'stuck';
export function adoptHandoffIfPresent(): Promise<HandoffOutcome> {
  // once per page: a second boot (React's dev double effect) must not spend the code again
  return (adopting ??= adoptOnce());
}
let adopting: Promise<HandoffOutcome> | null = null;

/** True when the url carries a handoff code, so boot waits on the redeem before it opens on anything. */
export function handoffInUrl(): boolean {
  return typeof window !== 'undefined' && !!handoffFromUrl(window.location.href);
}

/** The waits before each retry of a redeem the backend failed; one redeem may take REDEEM_TIMEOUT_MS. */
const REDEEM_RETRY_MS = [700, 1800];
const REDEEM_TIMEOUT_MS = 5000;
/** Worth asking again: the backend broke or was busy, or never answered (status 0). */
const retryable = (status: number) => status === 0 || status === 429 || status >= 500;
const pause = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

async function redeem(code: string, here: User | null): Promise<{ res: Response | null; attempts: number }> {
  const headers = { 'Content-Type': 'application/json', ...(here ? { Authorization: `Bearer ${await here.getIdToken()}` } : {}) };
  for (let attempt = 1; ; attempt++) {
    let res: Response | null = null;
    try {
      const signal = typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(REDEEM_TIMEOUT_MS) : undefined;
      res = await fetch(`${BACKEND}/api/kindling/handoff/redeem`, { method: 'POST', headers, body: JSON.stringify({ code }), signal });
    } catch { /* no answer: retried like a 5xx */ }
    if ((res && !retryable(res.status)) || attempt > REDEEM_RETRY_MS.length) return { res, attempts: attempt };
    await pause(REDEEM_RETRY_MS[attempt - 1]);
  }
}

async function adoptOnce(): Promise<HandoffOutcome> {
  if (typeof window === 'undefined') return 'none';
  const code = handoffFromUrl(window.location.href);
  if (!code) return 'none';
  const via = handoffViaFromUrl(window.location.href) ?? 'unknown';
  const emailLink = isSignInWithEmailLink(auth, window.location.href);
  const strip = () => {
    const next = withoutHandoff(window.location.href);
    if (next !== window.location.href) window.history.replaceState(window.history.state, '', next);
  };
  if (currentHandoff() === code) { strip(); return 'none'; }
  try {
    await auth.authStateReady();
    const here = auth.currentUser;
    if (here && !unsaved(here)) { strip(); return 'none'; }
    const { res, attempts } = await redeem(code, here);
    if (!res?.ok) {
      const status = res?.status ?? 0;
      const stuck = retryable(status);
      track('handoff_failed', { status, via, email_link: emailLink, attempts, stuck });
      if (stuck) return 'stuck';
      strip();
      return 'none';
    }
    const out = (await res.json()) as { uid: string; token?: string; email?: string };
    if (out.token && out.uid !== here?.uid) await signInWithCustomToken(auth, out.token);
    try {
      localStorage.setItem(HANDOFF_KEY, code);
      // the link this browser is finishing was sent to this address, and is a save, not a sign-in
      if (out.email) { localStorage.setItem(EMAIL_KEY, out.email); localStorage.removeItem(SIGNIN_KEY); }
    } catch { /* ignore */ }
    // The other half of handoff_offered: `uid` is the same on both, so PostHog can join the two browsers.
    // `had_identity` is somebody who had already started over here as somebody new (2026-10-16, Allen TX).
    track('handoff_landed', { uid: out.uid, via, had_identity: !!here, email_link: emailLink });
    strip();
    return 'adopted';
  } catch (err) {
    // the token was good but signing in with it was not (a dropped connection): the code has uses left
    console.warn('[kindling] handoff failed; keeping the code to try again', err);
    track('handoff_failed', { status: -1, via, email_link: emailLink, stuck: true });
    return 'stuck';
  }
}

/** HandoffStuck's way out: this browser as somebody new, the code dropped so the next load does not try it again. */
export function abandonHandoff() {
  window.location.replace(withoutHandoff(window.location.href));
}
